/**
 * MesAlerteDetailPage — détail d'une alerte côté administré (/mes-alertes/:id).
 *
 * Le back-office /grc/alertes/:id est réservé au staff (permission grc.alerte.read) ;
 * un citoyen y était refusé (403/404) et retombait au dashboard. Cette vue lit
 * l'alerte via la route publique GET /api/grc/alertes/:id/public (requireAuth
 * seulement) et n'affiche que les infos utiles au citoyen : titre, message, zone,
 * dates de début/fin, moment du déclenchement. Pas de détail d'envois.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import { Button, Badge, Card, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { AppHeader } from '@/components/AppHeader';
import { AlertTriangle, AlertOctagon, Bell, Info, ArrowLeft, MapPin, Clock } from 'lucide-react';
import type { GrcAlerte, GrcAlerteNiveau } from '@roronoa-collectivites/shared-types';

const NIVEAU: Record<GrcAlerteNiveau, { label: string; Icon: typeof Bell; variant: 'info' | 'warning' | 'error' }> = {
  info:   { label: 'Information', Icon: Info,          variant: 'info' },
  avis:   { label: 'Avis',        Icon: Bell,          variant: 'warning' },
  alerte: { label: 'Alerte',      Icon: AlertTriangle, variant: 'error' },
  crise:  { label: 'Crise',       Icon: AlertOctagon,  variant: 'error' },
};

function fmt(d: string | null | undefined): string | null {
  if (!d) return null;
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('fr-FR', {
    day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function MesAlerteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [alerte, setAlerte] = useState<GrcAlerte | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true); setError(null);
    (async () => {
      try {
        const a = await api.grc.alertes.getPublic(id);
        setAlerte(a);
      } catch (err) {
        setError(err instanceof ApiException ? err.payload.error : 'Alerte introuvable');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const n = alerte ? NIVEAU[alerte.niveau] : null;

  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Infos, alertes & services" accent="grc" />
      <main id="main-content" className="mx-auto max-w-[800px] px-4 py-5 animate-fade-in">
        <button
          type="button"
          onClick={() => navigate('/mes-alertes')}
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Retour aux alertes
        </button>

        {loading ? (
          <p className="text-sm text-ink-muted">Chargement…</p>
        ) : error || !alerte || !n ? (
          <Alert variant="error" icon={AlertTriangle}>{error ?? 'Alerte introuvable'}</Alert>
        ) : (
          <Card variant="glass" padded>
            <div className="flex items-center gap-3">
              <LogoMark icon={n.Icon} accent="grc" />
              <div>
                <SectionEyebrow as="span">Alerte municipale</SectionEyebrow>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-semibold text-ink">{alerte.titre}</h1>
                  <Badge variant={n.variant === 'error' ? 'urgent' : n.variant === 'warning' ? 'warning' : 'neutral'}>
                    {n.label}
                  </Badge>
                </div>
              </div>
            </div>

            <p className="mt-4 text-sm text-ink">{alerte.message}</p>
            {alerte.messageLong && (
              <p className="mt-3 whitespace-pre-line text-sm text-ink-muted">{alerte.messageLong}</p>
            )}

            <div className="mt-5 space-y-2 border-t border-line pt-4 text-sm text-ink-muted">
              {alerte.zoneGeographique && (
                <p className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 flex-shrink-0 text-grc" aria-hidden="true" />
                  Zone concernée : <span className="font-medium text-ink">{alerte.zoneGeographique}</span>
                </p>
              )}
              {fmt(alerte.dateDebut) && (
                <p className="flex items-center gap-2">
                  <Clock className="h-4 w-4 flex-shrink-0 text-grc" aria-hidden="true" />
                  Début : <span className="font-medium text-ink">{fmt(alerte.dateDebut)}</span>
                </p>
              )}
              {fmt(alerte.dateFin) && (
                <p className="flex items-center gap-2">
                  <Clock className="h-4 w-4 flex-shrink-0 text-grc" aria-hidden="true" />
                  Fin : <span className="font-medium text-ink">{fmt(alerte.dateFin)}</span>
                </p>
              )}
              {fmt(alerte.declencheAt) && (
                <p className="text-xs text-ink-subtle">Publiée le {fmt(alerte.declencheAt)}</p>
              )}
            </div>

            <div className="mt-5">
              <Button type="button" variant="secondary" size="sm" onClick={() => navigate('/mes-alertes')}>
                Voir toutes les infos de ma commune
              </Button>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
